import React, { useEffect, useState } from 'react';
import { 
  Activity, 
  FileText, 
  MapPin, 
  Brain, 
  BarChart3, 
  TrendingUp,
  Monitor,
  Users,
  Shield,
  ExternalLink,
  Lightbulb,
  BookOpen
} from 'lucide-react';
import TeamCarousel from './TeamCarousel';
import Header from './Header';
import ValueProposition from './ValueProposition';
import '../styles/MedicalMotion.css';

const MedicalMotionGraphic = () => {
  const [animationKey, setAnimationKey] = useState(0);

  // Restart animation every 12 seconds to keep it fresh
  useEffect(() => {
    const interval = setInterval(() => {
      setAnimationKey(prev => prev + 1);
    }, 12000);
    return () => clearInterval(interval);
  }, []);

  const inputDataTypes = [
    { icon: Activity, label: 'Vital Signs', delay: '0s', color: '#AC1F2D' },
    { icon: FileText, label: 'Clinical Notes', delay: '1s', color: '#657EE2' },
    { icon: MapPin, label: 'Location', delay: '2s', color: '#7688CE' },
    { icon: Users, label: 'Patient Records', delay: '3s', color: '#70799E' },
  ];

  const outputDashboards = [
    { icon: BarChart3, label: 'Analytics', delay: '5s', color: '#E46470' },
    { icon: Lightbulb, label: 'Suggestions', delay: '6s', color: '#F7909A' },
    { icon: Monitor, label: 'Documentation', delay: '7s', color: '#EF5350' },
    { icon: BookOpen, label: 'Knowledge', delay: '8s', color: '#AC1F2D' },
  ];

  return (
    <div className="medical-motion-container">
      <Header />
      <div className="background-grid"></div>
      


      {/* Main Animation Container */}
      <div className="animation-stage" key={animationKey}>
        
        {/* Input Data Sources - Left Side */}
        <div className="data-inputs">
          <div className="section-header">
            <h3 className="section-title">Real-Time Data</h3>
          </div>
          {inputDataTypes.map((dataType, index) => {
            const IconComponent = dataType.icon;
            return (
              <div 
                key={index} 
                className="data-source-item"
                style={{ 
                  '--animation-delay': dataType.delay,
                  '--item-color': dataType.color
                }}
              >
                <div className="data-icon-wrapper">
                  <IconComponent size={32} className="data-icon" />
                </div>
                <span className="data-label">{dataType.label}</span>
                <div className="flow-line input-flow"></div>
              </div>
            );
          })}
        </div>

        {/* Central Data Foundry */}
        <div className="data-foundry">
          <div className="foundry-core">
            <div className="foundry-inner">
              <Brain size={48} className="foundry-brain" />
              <div className="processing-rings">
                <div className="ring ring-1"></div>
                <div className="ring ring-2"></div>
                <div className="ring ring-3"></div>
              </div>
            </div>
            <div className="foundry-particles"></div>
          </div>
          <div className="foundry-title-container">
            <img 
              src="/images/logos/foundry-logo.png" 
              alt="AI Data Foundry" 
              className="foundry-logo"
              onError={(e) => {
                // Fallback if logo doesn't exist
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <h3 className="foundry-title" style={{ display: 'none' }}>AI Data Foundry</h3>
          </div>
          <p className="foundry-subtitle">Agentic universal harmonizer and machine intelligence platform for asking the right questions at the right time</p>
        </div>

        {/* Output AI Dashboard - Right Side */}
        <div className="data-outputs">
          <div className="section-title-container">
            <img 
              src="/images/logos/pathways-logo.png" 
              alt="AI-Powered Insights" 
              className="monitor-logo"
              onError={(e) => {
                // Fallback if logo doesn't exist
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <h3 className="section-title" style={{ display: 'none' }}>AI-Powered Insights</h3>
            <p className="monitor-subtitle">AI-powered workflow orchestration for acting on the right answers</p>
          </div>
          {outputDashboards.map((dashboard, index) => {
            const IconComponent = dashboard.icon;
            return (
              <div 
                key={index} 
                className="dashboard-item"
                style={{ 
                  '--animation-delay': dashboard.delay,
                  '--item-color': dashboard.color
                }}
              >
                <div className="flow-line output-flow"></div>
                <div className="dashboard-icon-wrapper">
                  <IconComponent size={32} className="dashboard-icon" />
                </div>
                <span className="dashboard-label">{dashboard.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Data Flow Visualization */}
      <div className="flow-visualization">
        <div className="flow-particles">
          {[...Array(20)].map((_, i) => (
            <div 
              key={i} 
              className="particle"
              style={{ 
                '--particle-delay': `${i * 0.3}s`,
                '--particle-color': i % 2 === 0 ? '#AC1F2D' : '#657EE2'
              }}
            />
          ))}
        </div>
      </div>

      {/* Value Proposition Section */}
      <ValueProposition />

      {/* Team Carousels Section */}
      <div className="team-section">
        <TeamCarousel />
      </div>

      {/* Company Links Section */}
      <div className="company-links">
        <div className="links-container">
          <a 
            href="https://viewfromthemtn.substack.com/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="company-link"
          >
            <div className="link-content">
              <h4>Blog</h4>
              <p>Stay updated with our latest insights and research</p>
            </div>
            <ExternalLink size={20} />
          </a>
          <a 
            href="https://linkedin.com/company/themtn" 
            target="_blank" 
            rel="noopener noreferrer"
            className="company-link"
          >
            <div className="link-content">
              <h4>Follow</h4>
              <p>Connect with us on LinkedIn for company updates</p>
            </div>
            <ExternalLink size={20} />
          </a>
          <div 
            className="company-link"
            onClick={() => {
              // Try to open email client
              const mailtoLink = "mailto:info@themtn.ai?subject=Partnership%20Inquiry&body=Hello,%0A%0AI'm interested in learning more about partnership opportunities.%0A%0ABest regards,";
              
              // Create a temporary link and click it
              const link = document.createElement('a');
              link.href = mailtoLink;
              link.style.display = 'none';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              
              // Fallback: if mailto doesn't work, copy email to clipboard
              setTimeout(() => {
                navigator.clipboard.writeText('info@themtn.ai').then(() => {
                  alert('Email copied to clipboard: info@themtn.ai\n\nIf your email client didn\'t open, please paste this email address into your email client.');
                }).catch(() => {
                  alert('Please email us at: info@themtn.ai');
                });
              }, 1000);
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="link-content">
              <h4>Contact</h4>
              <p>Reach out for questions regarding partnership opportunities</p>
            </div>
            <ExternalLink size={20} />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="motion-footer">
        <p>Medical Timeseries Networks offers real-time medical data processing with AI-driven analytics</p>
      </footer>
    </div>
  );
};

export default MedicalMotionGraphic;